import { DoNotDisturbOn, MailOutline } from '@mui/icons-material'
import { Box, Button, Divider, Link, Skeleton, Typography, useTheme } from '@mui/material'
import { capitalize } from 'lodash'
import moment from 'moment'
import { useMemo } from 'react'
import { useAuth } from 'src/context/AuthContext'
import { useBillingContext } from 'src/context/BillingContext'
import { usePrivateLayoutDrawerContext } from 'src/context/PrivateLayoutDrawerContext'
import { LoopModule } from 'src/services/openApiV2'
import { ModuleNameMapping, Modules } from 'src/utils/config/config'
import { formatCurrency } from 'src/utils/functions'
import { loopModuleToModuleKeyMap } from 'src/utils/functions/moduleUtils'
import { pluralize } from 'src/utils/functions/pluralize'
import useLayoutDimension from 'src/utils/hooks/useLayoutDimension'
import { CategoryIconMappingColored } from './components/PrivateLayoutDrawer/components/CategoryIconMapping'
import { LOOP_SUPPORT_EMAIL } from 'src/utils/config/config'

const moduleTexts = [
  {
    key: LoopModule.RECOVER,
    title: ModuleNameMapping[LoopModule.RECOVER],
    icon: CategoryIconMappingColored[loopModuleToModuleKeyMap[LoopModule.RECOVER]],
    text: `${capitalize(Modules.Chargebacks)} recovery and dispute automation has stopped. You are missing out on revenue recovery.`
  },
  {
    key: LoopModule.GUARD,
    title: ModuleNameMapping[LoopModule.GUARD],
    icon: CategoryIconMappingColored[loopModuleToModuleKeyMap[LoopModule.GUARD]],
    text: 'Store availability monitoring is paused. Your stores may go down without immediate detection and restoration.'
  },
  {
    key: LoopModule.BALANCE,
    title: ModuleNameMapping[LoopModule.BALANCE],
    icon: CategoryIconMappingColored[loopModuleToModuleKeyMap[LoopModule.BALANCE]],
    text: 'Accounting automation and marketplace reconciliation has stopped. Your books are not up-to-date.'
  },
  {
    key: LoopModule.TRUROI,
    title: ModuleNameMapping[LoopModule.TRUROI],
    icon: CategoryIconMappingColored[loopModuleToModuleKeyMap[LoopModule.TRUROI]],
    text: 'Marketing insights and deeper insights on campaign and competition analysis are disabled.'
  },
  {
    key: LoopModule.REENG,
    title: ModuleNameMapping[LoopModule.REENG],
    icon: CategoryIconMappingColored[loopModuleToModuleKeyMap[LoopModule.REENG]],
    text: 'Automated review responses and rating insights is paused. Your customer engagement is at risk.'
  }
]

const BillingExpired = () => {
  const { activeBillingConfig } = useBillingContext()
  const { categorizedRoutes } = usePrivateLayoutDrawerContext()
  const { categoryLoading } = useAuth()
  const { headerHeightPx } = useLayoutDimension()

  const theme = useTheme()

  const activeModuleTexts = useMemo(() => {
    return moduleTexts.filter((module) => categorizedRoutes.find((category) => category.category.key === loopModuleToModuleKeyMap[module.key]))
  }, [categorizedRoutes])

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={{ xs: 2, sm: 3, md: 4 }}
      p={{ xs: 2, sm: 3, md: 3 }}
      px={{ xs: 2, sm: 4, md: 8, lg: 12 }}
      minHeight={`calc(100vh - ${headerHeightPx})`}
      sx={{
        background: 'linear-gradient(180deg, rgba(246, 246, 246, 0.28) -10.48%, rgba(25, 110, 130, 0.28) 88.18%)'
      }}>
      <Box
        width="100%"
        maxWidth={{ xs: '100%', sm: '600px', md: '800px', lg: '1000px', xl: '1200px' }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={{ xs: 1.5, sm: 2 }}
        py={{ xs: 1.5, sm: 2 }}
        px={{ xs: 2, sm: 4, md: 6 }}
        border="1px solid #E6E6E6"
        borderRadius={2}
        sx={{
          background: 'linear-gradient(180deg, #FFFFFF66 0%, #FFFFFFE5 100%)'
        }}>
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={1}>
          <DoNotDisturbOn
            sx={{
              fontSize: { xs: 40, sm: 50, md: 60 },
              color: '#CD3546'
            }}
          />
          <Typography
            fontSize={{ xs: 16, sm: 17, md: 18 }}
            fontWeight={600}
            color="#CD3546"
            textAlign="center"
            px={{ xs: 1, sm: 0 }}>
            Your loop access is temporarily on hold
          </Typography>

          <Typography
            fontSize={{ xs: 12, sm: 13, md: 14 }}
            fontWeight={400}
            color="#000"
            textAlign="center"
            px={{ xs: 1, sm: 0 }}>
            All automated services have been paused. Your business operations are at risk until payment is completed.
          </Typography>
        </Box>

        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          bgcolor="#BBD3D93D"
          border="1px solid #E6E6E6"
          px={{ xs: 2, sm: 3, md: 4 }}
          py={1}
          borderRadius={2}
          gap={1}>
          {categoryLoading
            ? moduleTexts.map((_, index) => (
                <Box
                  key={index}
                  display="flex"
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  gap={{ xs: 1.5, sm: 2 }}
                  sx={{
                    py: { xs: 1.5, sm: 1 },
                    px: { xs: 2, sm: 3, md: 4 }
                  }}>
                  <Box
                    minWidth={{ xs: 'auto', sm: '150px' }}
                    width={{ xs: '100%', sm: 'auto' }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={{ xs: 1.5, sm: 2 }}>
                      <Skeleton
                        variant="rounded"
                        width={120}
                        height={24}
                      />
                    </Box>
                  </Box>

                  <Box
                    width="100%"
                    display="flex"
                    flexDirection="column"
                    gap={0.5}>
                    <Skeleton
                      variant="rounded"
                      width="100%"
                      height={30}
                    />
                  </Box>
                </Box>
              ))
            : activeModuleTexts.map((module, index) => (
                <>
                  <Box
                    key={module.key + index}
                    display="flex"
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    gap={{ xs: 1.5, sm: 2 }}
                    sx={{
                      py: { xs: 1.5, sm: 2 },
                      px: { xs: 2, sm: 3, md: 4 }
                    }}>
                    <Box
                      minWidth={{ xs: 'auto', sm: '150px' }}
                      width={{ xs: '100%', sm: 'auto' }}>
                      <Box
                        display="flex"
                        alignItems="center"
                        height="100%"
                        gap={{ xs: 1.5, sm: 2 }}>
                        <module.icon
                          fill="#196E82"
                          width="20px !important"
                          height="20px !important"
                        />

                        <Typography
                          fontSize={{ xs: 14, sm: 15, md: 16 }}
                          fontWeight={600}
                          color="#196E82"
                          textAlign={{ xs: 'left', sm: 'center' }}
                          lineHeight="initial">
                          {module.title}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      width="100%"
                      display="flex"
                      alignItems="center">
                      <Typography
                        fontSize={{ xs: 12, sm: 13, md: 14 }}
                        fontWeight={400}
                        color="#000A"
                        lineHeight="initial">
                        {module.text}
                      </Typography>
                    </Box>
                  </Box>
                </>
              ))}
        </Box>

        <Box
          mt={1}
          border="1px solid #E6E6E6"
          borderRadius={2}
          p={{ xs: 1.5, sm: 2 }}
          pt={{ xs: 1.5, sm: 2 }}
          width="100%"
          display="flex"
          flexDirection="column"
          gap={2}
          bgcolor="#FFFFFFEB">
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            px={{ xs: 2, sm: 4, md: 6 }}>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
              gap={{ xs: 0.5, sm: 2 }}>
              <Typography
                fontSize={{ xs: 14, sm: 15, md: 16 }}
                fontWeight={400}
                color="#000">
                Invoice date:
              </Typography>

              <Typography
                fontSize={{ xs: 14, sm: 15, md: 16 }}
                fontWeight={600}
                color="#CD3546"
                minWidth={{ xs: 'auto', sm: '160px' }}
                textAlign={{ xs: 'left', sm: 'right' }}>
                {activeBillingConfig?.lastDate?.format('DD MMMM YYYY')}
              </Typography>
            </Box>

            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
              gap={{ xs: 0.5, sm: 2 }}>
              <Typography
                fontSize={{ xs: 14, sm: 15, md: 16 }}
                fontWeight={400}
                color="#000">
                Days overdue:
              </Typography>

              <Typography
                fontSize={{ xs: 14, sm: 15, md: 16 }}
                fontWeight={600}
                color="#CD3546"
                minWidth={{ xs: 'auto', sm: '160px' }}
                textAlign={{ xs: 'left', sm: 'right' }}>
                {moment().diff(activeBillingConfig?.lastDate, 'days')} {pluralize('day', moment().diff(activeBillingConfig?.lastDate, 'days'))}
              </Typography>
            </Box>

            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
              gap={{ xs: 0.5, sm: 2 }}>
              <Typography
                fontSize={{ xs: 14, sm: 15, md: 16 }}
                color="#000"
                fontWeight={600}>
                Amount due:
              </Typography>

              <Typography
                fontSize={{ xs: 14, sm: 15, md: 16 }}
                fontWeight={600}
                color="#CD3546"
                minWidth={{ xs: 'auto', sm: '160px' }}
                textAlign={{ xs: 'left', sm: 'right' }}>
                {formatCurrency(activeBillingConfig?.paymentAmount ?? 0)}
              </Typography>
            </Box>
          </Box>

          <Button
            onClick={() => window.open(activeBillingConfig?.paymentLink, '_blank')}
            sx={{
              width: '100%',
              height: '65px',
              py: 0,
              fontSize: { xs: 12, sm: 14, md: 16 },
              bgcolor: '#3D9A38',
              color: '#fff',
              '&:hover': {
                bgcolor: '#256e21'
              }
            }}>
            RESTORE ACCESS NOW: Pay {formatCurrency(activeBillingConfig?.paymentAmount ?? 0)}
          </Button>
        </Box>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={1}
        px={{ xs: 2, sm: 0 }}>
        <Typography
          fontSize={{ xs: 10, sm: 11, md: 12 }}
          fontWeight={400}
          color="#5E5E5E"
          textAlign="center">
          Need assistance? Mail us, our team will promptly get back to you!
        </Typography>

        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          alignItems="center"
          gap={{ xs: 1, sm: 1 }}
          textAlign={{ xs: 'center', sm: 'left' }}>
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}>
            <MailOutline
              sx={{
                fontSize: { xs: 16, sm: 18, md: 20 },
                color: '#5E5E5E'
              }}
            />
            <Typography
              color="#5E5E5E"
              fontSize={{ xs: 12, sm: 13, md: 14 }}>
              Support:{' '}
              <Link
                href={`mailto:${LOOP_SUPPORT_EMAIL}`}
                style={{ color: '#5E5E5E', textDecorationColor: '#5E5E5E', fontWeight: 600 }}>
                support@tryloop.ai
              </Link>
            </Typography>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
          />

          <Typography
            color="#5E5E5E"
            fontSize={{ xs: 12, sm: 13, md: 14 }}>
            Billing:{' '}
            <Link
              href="mailto:billing@tryloop.ai"
              style={{ color: '#5E5E5E', textDecorationColor: '#5E5E5E', fontWeight: 600 }}>
              billing@tryloop.ai
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default BillingExpired
